// ** React Imports
import { useState, ChangeEvent } from 'react'

// ** MUI Imports
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  styled,
  TableSortLabel,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { useTranslation } from 'react-i18next'
import { formatDate } from '@/@core/utils/utils'

interface Column {
  id: string
  label: string
  minWidth?: number
  align?: 'right' | 'left' | 'center'
  format?: (value: any, row: any) => string
  renderHeader?: () => string
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }

  if (b[orderBy] > a[orderBy]) {
    return 1
  }

  return 0
}

type Order = 'asc' | 'desc'

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])

    if (order !== 0) {
      return order
    }

    return a[1] - b[1]
  })

  return stabilizedThis.map((el) => el[0])
}

interface EnhancedTableProps {
  numSelected: number
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  order: Order
  orderBy: string
  rowCount: number
  columns: Column[]
  isSelected: boolean
}

interface Data {
  id: number
  name: string
  code: string
  size: number
  density: number
  population: number
}

function createData(
  name: string,
  code: string,
  population: number,
  size: number,
  id: number
): Data {
  const density = population / size

  return { name, code, population, size, density, id }
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    columns,
    isSelected
  } = props

  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  return (
    <TableHead>
      <TableRow
        sx={{
          bgcolor: '#F7F7F8'
        }}
      >
        {isSelected ? (
          <TableCell
            sx={{
              bgcolor: 'action.hover',
              position: 'static',
              fontSize: '13px',
              fontWeight: 700,
              padding: '8px 16px',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'select all desserts'
              }}
            />
          </TableCell>
        ) : (
          ''
        )}

        {columns.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              bgcolor: 'action.hover',
              position: 'static',
              fontSize: '13px',
              fontWeight: 700,
              padding: '8px 16px',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id as any)}
              sx={{
                textTransform: 'capitalize',
                color: 'text.secondary'
              }}
            >
              <Typography minWidth={40} sx={{ fontWeight: 600 }}>
                {headCell.renderHeader
                  ? headCell.renderHeader()
                  : headCell.label}
              </Typography>

              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const Index = React.forwardRef(
  (
    props: {
      columns?: Column[]
      rows?: any[]
      sx?: any
      isSelected?: boolean
      maxHeight?: number | string
      stickyHeader?: boolean
    },
    ref
  ) => {
    const { t } = useTranslation()

    // ** States
    const [page, setPage] = useState<number>(0)
    const [rowsPerPage, setRowsPerPage] = useState<number>(10)
    const [order, setOrder] = React.useState<Order>('asc')
    const [orderBy, setOrderBy] = React.useState<keyof Data>('id')
    const [selected, setSelected] = React.useState<readonly number[]>([])

    let rows = [] as any[]
    let columns = [] as Column[]
    if (props.rows) rows = props.rows
    else
      rows = [
        createData('India', 'IN', 1324171354, 3287263, 0),
        createData('China', 'CN', 1403500365, 9596961, 1),
        createData('Italy', 'IT', 60483973, 301340, 2),
        createData('United States', 'US', 327167434, 9833520, 3),
        createData('Canada', 'CA', 37602103, 9984670, 4),
        createData('Australia', 'AU', 25475400, 7692024, 5),
        createData('Germany', 'DE', 83019200, 357578, 6),
        createData('Ireland', 'IE', 4857000, 70273, 7),
        createData('Mexico', 'MX', 126577691, 1972550, 8),
        createData('Japan', 'JP', 126317000, 377973, 9),
        createData('France', 'FR', 67022000, 640679, 10)
      ]

    if (props.columns) columns = props.columns
    else
      columns = [
        { id: 'id', label: 'Sample ID', minWidth: 170 },
        { id: 'name', label: 'Name', minWidth: 100 },
        {
          id: 'projectName',
          label: 'Project',
          minWidth: 170,
          align: 'left'
        },
        {
          id: 'createdAt',
          label: 'Submitted Date',
          minWidth: 170,
          align: 'left',
          format: (value) => formatDate(value) as string
        },
        {
          id: 'clinicalInformation',
          label: 'clinicalInformation',
          minWidth: 170,
          align: 'left',
          format: ((value: any) => {
            return (
              <Box>
                <HtmlTooltip
                  title={value}
                  placement="top"
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: '500px !important'
                    }
                  }}
                >
                  <Typography
                    maxWidth={320}
                    sx={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {value}
                  </Typography>
                </HtmlTooltip>
              </Box>
            )
          }) as any
        },
        {
          id: 'density',
          label: 'View',
          minWidth: 170,
          align: 'left'
        }
      ]

    const handleSelectAllClick = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      //

      if (event.target.checked) {
        const newSelected = rows.map((n, index) => n.id)

        setSelected(newSelected)

        return
      }

      setSelected([])
    }

    const handleRequestSort = (
      event: React.MouseEvent<unknown>,
      property: keyof Data
    ) => {
      const isAsc = orderBy === property && order === 'asc'

      setOrder(isAsc ? 'desc' : 'asc')

      setOrderBy(property)
    }

    const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
      const selectedIndex = selected.indexOf(id)
      let newSelected: readonly number[] = []

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id)
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1))
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1))
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        )
      }

      setSelected(newSelected)
    }

    const visibleRows = React.useMemo(
      () => stableSort(rows, getComparator(order, orderBy)),
      [order, orderBy]
    )

    const maxH = props.maxHeight ?? 440
    const sticky = props.stickyHeader ?? true
    return (
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: maxH,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          overflow: maxH === 'none' ? 'visible' : 'auto',
          '& .MuiTableHead-root': {
            position: 'sticky',
            top: 0,
            zIndex: 111,
            opacity: 1,
            backgroundColor: '#fff'
          },
          ...props.sx
        }}
      >
        <Table
          stickyHeader={sticky}
          aria-label="sticky table"
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: '1px solid',
              borderColor: 'divider'
            }
          }}
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            columns={columns}
            isSelected={Boolean(props.isSelected)}
          />

          <TableBody>
            {visibleRows.map((row: any, index) => {
              const isItemSelected = selected.includes(row.id)
              const labelId = `enhanced-table-checkbox-${index}`

              return (
                <TableRow
                  sx={{
                    bgcolor: row.isUPD
                      ? 'rgb(255,215,0)'
                      : index % 2
                      ? 'action.hover'
                      : 'background.default'
                  }}
                  onClick={(event) => handleClick(event, row.id)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id ?? index}
                  selected={isItemSelected}
                >
                  {props.isSelected ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          'aria-labelledby': labelId
                        }}
                      />
                    </TableCell>
                  ) : (
                    ''
                  )}

                  {columns.map((column) => {
                    const value = row[column.id]

                    if (column.label === 'View') {
                      return (
                        <TableCell
                          sx={{
                            fontSize: '14px',
                            paddingTop: '8px !important',
                            paddingBottom: '8px !important'
                          }}
                          key={column.id}
                          align={column.align}
                        >
                          <Button
                            sx={{
                              color: 'white !important'
                            }}
                            variant="contained"
                            size="small"
                            onClick={() => {
                              window.open(`/samples/SampleTabs/${row.id}/`)
                            }}
                          >
                            {t('view')}
                          </Button>
                        </TableCell>
                      )
                    } else
                      return (
                        <TableCell
                          sx={{
                            fontSize: '14px',
                            paddingTop: '8px !important',
                            paddingBottom: '8px !important'
                          }}
                          key={column.id}
                          align={column.align}
                        >
                          {column.format ? column.format(value, row) : value}
                        </TableCell>
                      )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }
)
export default Index
